import * as React from "react";
import classNames from "classnames";
import { FC, useEffect, useRef, useState } from "react";
import { useAppContext } from "../Contexts/AppContext";
import { PopupButton } from "../Elements/PopupButton";
import { ApplicantForm } from "../Forms/ApplicantForm";
import { useRegistrationContext } from "../Contexts/RegistrationContext";
import {
    IAttachmentFile,
    IAttachmentPath,
    printPdf,
    requestObjectIds,
    sendEmail,
    updateFeatures,
    uploadFile
} from "../Map/rest";
import { TermForm } from "../Forms/TermForm";
import { SummaryForm } from "../Forms/SummaryForm";
import { UserConsentCheck } from "./UserConsent";
import { DrawingForm } from "../Forms/DrawingForm";
import { PageId, Result, UploadState, PrintType, AttachmentMode } from "../Map/enums";
import { Feature } from "ol";
import { HelpButton } from "../Elements/HelpButton";
import { ISubmitStatus } from "./SummarySection";
import { SubmitForm } from "./SubmitForm";
import ConexusLogo from "../img/conexus-logo.svg";
import ConexusFooterLogo from "../img/logo-footer.svg";
import "./Registration.scss";
import "../Views/FullscreenView.scss";

export const Registration: FC = () => {
    const [submitStatus, setSubmitStatus] = useState<ISubmitStatus>();
    const [registrationCompleted, setRegistrationCompleted] = useState<Result | undefined>(undefined);

    const {
        page,
        formFeature,
        drawnFeatures,
        attachments,
        attachedProcurations,
        getPrintData
    } = useRegistrationContext();

    const onSubmit = async () => {
        /*** Upload drawn geometries */
        const geometriesUploaded =
            drawnFeatures && drawnFeatures.length ? await uploadFeatures() : await Promise.resolve();
        if (!geometriesUploaded) {
            setSubmitStatus({ name: UploadState.UploadGeometries, result: Result.Error });
            setRegistrationCompleted(Result.Error);
            console.log("Upload geometries error");
            return;
        }
        setSubmitStatus({ name: UploadState.UploadGeometries, result: Result.Success });
        console.log("geometries uploaded");

        /*** Upload attachments */
        const attachmentsUploaded = await uploadAttachments();
        const emailAttachments: IAttachmentFile[] = attachmentsUploaded.length
            ? attachmentsUploaded.map((fileId) => ({
                  type: AttachmentMode.File,
                  endpoint: "FileProvider",
                  resourceId: fileId
              }))
            : [];

        /*** Submit Form */
        formFeature.set("FORMINPUTSTATE", 110, true);
        const featureUpdate = await updateFeatures([formFeature], "2");
        if (!featureUpdate || !featureUpdate[0].success) {
            setSubmitStatus({ name: UploadState.SubmitForm, result: Result.Error });
            setRegistrationCompleted(Result.Error);
            console.log("Submit form error");
            return;
        }
        setSubmitStatus({ name: UploadState.SubmitForm, result: Result.Success });
        console.log("Form feature submitted", featureUpdate);

        /*** Create PDF */
        const printData = await getPrintData();
        const pdfResult = await printPdf(printData, PrintType.File);
        // const pdfResult = await printPdf(printData, PrintType.Path);
        if (pdfResult && pdfResult.status === 0) {
            setSubmitStatus({ name: UploadState.CreatePdf, result: Result.Error });
            setRegistrationCompleted(Result.Error);
            return;
        }
        setSubmitStatus({ name: UploadState.CreatePdf, result: Result.Success });
        const file = new File([pdfResult.data], "pieteikums.pdf", { type: "application/pdf" });
        console.log("pdf print result", pdfResult);
        // uncomment, to open PDF in new tab
        // const obj_url = URL.createObjectURL(file);
        // window.open(obj_url, "aaa");
        // URL.revokeObjectURL(obj_url);

        /*** Upload PDF */
        const uploadedPdfFileName = await uploadPdf(file);
        if (!uploadedPdfFileName) {
            setSubmitStatus({ name: UploadState.UploadPdf, result: Result.Error });
            setRegistrationCompleted(Result.Error);
            return;
        }
        setSubmitStatus({ name: UploadState.UploadPdf, result: Result.Success });

        /*** Send Email with attached PDF */
        emailAttachments.push({
            type: AttachmentMode.File,
            endpoint: "FileProvider", // endpoint name, taken from Mailer appsettings.config
            resourceId: uploadedPdfFileName
        });

        /*** Send Email with PDF as path */
        // const emailAttachment: IAttachmentPath = {
        //     type: AttachmentMode.Path,
        //     // url: pdfResult.data.path
        //     url: uploadedPdfFileName
        // };

        const emailResult = await sendEmail(formFeature.get("APPLICANTEMAIL"), emailAttachments);
        console.log("send email result", emailResult);
        if (emailResult && emailResult.status === 0) {
            setSubmitStatus({ name: UploadState.SendEmail, result: Result.Error });
            setRegistrationCompleted(Result.Error);
            return;
        }
        setSubmitStatus({ name: UploadState.SendEmail, result: Result.Success });
        setRegistrationCompleted(Result.Success);
    };

    const uploadFeatures = async () => {
        return Promise.all(
            drawnFeatures.map(async (feature) => {
                feature.set("FORMGLOBALID", formFeature.get("GLOBALID"), true);
                return await requestObjectIds([feature], getLayerId(feature));
            })
        );
    };

    const uploadAttachments = async () => {
        const uploadedFileIds: string[] = [];
        const dt = new DataTransfer();
        attachedProcurations && Array.from(attachedProcurations).map((file) => dt.items.add(file));
        attachments && Array.from(attachments).map((file) => dt.items.add(file));

        if (!dt.files.length) {
            return Promise.resolve([]);
        }

        const fileFeatures: Feature[] = Array.from(dt.files, (file) => {
            const fileFeature = new Feature();
            fileFeature.set("FILEDESCRIPTION", file.name);
            return fileFeature;
        });
        console.log("fileFeatures", fileFeatures);

        const fileFeatureOIDs = await requestObjectIds(fileFeatures, "3");
        return Promise.all(
            fileFeatureOIDs.map(async (oid, i) =>
                uploadFile(dt.files[i], oid.objectId.toString()).then((uploadResult) => {
                    if (uploadResult && uploadResult.status === 200 && uploadResult.data.fileName) {
                        fileFeatures[i].setProperties({
                            OBJECTID: oid.objectId,
                            FILEID: uploadResult.data.fileName,
                            FORMGLOBALID: formFeature.get("GLOBALID")
                        });
                        uploadedFileIds.push(uploadResult.data.fileName);
                        setSubmitStatus({ name: dt.files[i].name, result: Result.Success });
                    } else {
                        setSubmitStatus({ name: dt.files[i].name, result: Result.Error });
                    }
                })
            )
        )
            .then(() => updateFeatures(fileFeatures, "3"))
            .then(() => uploadedFileIds);
    };

    const uploadPdf = async (file: File): Promise<string> => {
        const fileFeature: Feature = new Feature();
        fileFeature.set("FILEDESCRIPTION", file.name);

        const fileFeatureOIDs = await requestObjectIds([fileFeature], "3");
        const oid = fileFeatureOIDs[0];

        const uploadResult = await uploadFile(file, oid.objectId.toString());
        if (!uploadResult || uploadResult.status !== 200 || !uploadResult.data.fileName) {
            return;
        }

        fileFeature.setProperties({
            OBJECTID: oid.objectId,
            FILEID: uploadResult.data.fileName,
            FORMGLOBALID: formFeature.get("GLOBALID")
        });
        await updateFeatures([fileFeature], "3");

        return uploadResult.data.fileName;
    };

    useEffect(() => {
        if (page === PageId.ApplicantForm) {
            setSubmitStatus(undefined);
            setRegistrationCompleted(undefined);
        }
        page === PageId.Submit && onSubmit();
    }, [page]);

    return (
        <>
            <div className="fs-view-container">
                <div className={"fs-container unselectable conexus-container"}>
                    <div className="conexus-header">
                        <img className="conexus-logo" src={ConexusLogo}></img>
                        {page === PageId.ApplicantForm && (
                            <h1 className="conexus-header-title">Iesniegums darbu veikšanai aizsargjoslā</h1>
                        )}
                        <div className="green-line"></div>
                    </div>
                    <div className="fs-view">
                        <RegistrationHeader></RegistrationHeader>
                        <RegistrationBody
                            submitStatus={submitStatus}
                            registrationCompleted={registrationCompleted}
                        ></RegistrationBody>
                        <RegistrationFooter registrationCompleted={registrationCompleted}></RegistrationFooter>
                    </div>
                </div>
            </div>
            <ConexusFooter></ConexusFooter>
        </>
    );
};

const RegistrationHeader = (): JSX.Element => {
    const { page } = useRegistrationContext();

    return (
        <h1 className="fs-header">
            <>
                {page === PageId.ApplicantForm && <div>Dati par iesniedzēju</div>}
                {page === PageId.TermForm && <div>Dati par plānotajiem darbiem</div>}
                {page === PageId.DrawingForm && (
                    <>
                        <div>Plānoto darbu vieta</div>
                        <HelpButton></HelpButton>
                    </>
                )}
                {page === PageId.SummaryForm && <div>Pārskats</div>}
                {page === PageId.Submit && <div>PIETEIKUMA IESNIEGŠANA</div>}
            </>
        </h1>
    );
};

const RegistrationBody = ({
    submitStatus,
    registrationCompleted
}: {
    submitStatus: ISubmitStatus;
    registrationCompleted: Result | undefined;
}): JSX.Element => {
    const { page, setUserConsent, overlay } = useRegistrationContext();

    const content = (
        <>
            {page === PageId.ApplicantForm && (
                <>
                    <ApplicantForm></ApplicantForm>
                    <UserConsentCheck onChange={setUserConsent}></UserConsentCheck>
                </>
            )}
            {page === PageId.TermForm && <TermForm></TermForm>}
            {page === PageId.DrawingForm && <DrawingForm></DrawingForm>}
            {page === PageId.SummaryForm && <SummaryForm></SummaryForm>}
            {page === PageId.Submit && (
                <SubmitForm submitStatus={submitStatus} completed={registrationCompleted}></SubmitForm>
            )}
        </>
    );
    return (
        <div className="fs-content">
            <div
                className={classNames("fs-content-data", {
                    "summary-container": page === PageId.SummaryForm,
                    "submit-container": page === PageId.Submit
                })}
            >
                {content}
            </div>
            {overlay ? <FullscreenOverlay overlay={overlay}></FullscreenOverlay> : null}
        </div>
    );
};

const RegistrationFooter = ({ registrationCompleted }: { registrationCompleted: Result | undefined }): JSX.Element => {
    const containerRef = useRef<HTMLDivElement>();
    const { page, userConsent, onFormBack, onFormProceed, formFeature } = useRegistrationContext();

    useEffect(() => {
        setTimeout(() => {
            const captcha = document.getElementsByClassName("grecaptcha-badge")[0].cloneNode(true);
            containerRef.current && containerRef.current.append(captcha);
        }, 200);
    }, []);

    const content = (
        <>
            {page === PageId.ApplicantForm && (
                <>
                    <div className="captcha-container" ref={containerRef}></div>
                    <PopupButton
                        title={"Turpināt"}
                        classname={"green"}
                        onClick={onFormProceed}
                        disabled={!userConsent || !formFeature}
                    ></PopupButton>
                </>
            )}
            {(page === PageId.TermForm || page === PageId.DrawingForm) && (
                <>
                    <PopupButton title={"Atpakaļ"} classname={"light"} onClick={onFormBack}></PopupButton>
                    <PopupButton title={"Turpināt"} classname={"green"} onClick={onFormProceed}></PopupButton>
                </>
            )}
            {page === PageId.SummaryForm && (
                <>
                    <PopupButton title={"Atpakaļ"} classname={"light"} onClick={onFormBack}></PopupButton>
                    <PopupButton title={"Iesniegt"} classname={"green"} onClick={onFormProceed}></PopupButton>
                </>
            )}
            {page === PageId.Submit && (
                <PopupButton
                    title={"Jauns pieteikums"}
                    classname={"green"}
                    onClick={onFormProceed}
                    disabled={registrationCompleted === undefined}
                ></PopupButton>
            )}
        </>
    );
    return (
        <div className="fs-footer">
            <div className="fs-row">{content}</div>
        </div>
    );
};

const ConexusFooter = () => (
    <div className="conexus-footer">
        <div className="conexus-container">
            <img className="conexus-logo" src={ConexusFooterLogo}></img>
            <div className="contact">
                <p>
                    <a>
                        <b>KONTAKTI</b>
                    </a>
                </p>
                <p>
                    AS "Conexus Baltic Grid"
                    <br></br>
                    Stigu iela 14, Rīga, LV-1021, Latvija
                    <br></br>
                    +371 67 087 900
                    <br></br>
                    <a className="url" href="mailto:info@conexus.lv">
                        info@conexus.lv
                    </a>
                </p>
                <div className="center">© AS “Conexus Baltic Grid”, 2022 </div>
            </div>
        </div>
    </div>
);

export interface IFullscreenViewOverlay {
    isLoading?: boolean;
    info?: string;
    warning?: string;
}

const FullscreenOverlay = ({ overlay }: { overlay: IFullscreenViewOverlay }) => (
    <div className="fs-overlay">
        {overlay.isLoading && (
            <div className="fs-loading">
                <div className="spinner"></div>
            </div>
        )}
        {overlay.info && <div className="fs-overlay-info">{overlay.info}</div>}
        {overlay.warning && <div className="fs-overlay-warning">{overlay.warning}</div>}
    </div>
);

const getLayerId = (feature) => {
    const geometryType = feature.getGeometry().getType();
    return geometryType === "Point" ? "0" : "1";
};
